import type React from 'react';
import { PureComponent, type ReactNode } from 'react';

import { ICONS } from '../../../icons/icon';
import Button from '../../../ui/button/button';

import styles from './text_multi.module.css';

export type ValueType = string[];

type FieldProps = {
  index: number;
  name: string;
  value: string;
  onRemove: (index: number) => void;
  onChange: (index: number, value: string) => void;
  disabled?: boolean;
};

class Field extends PureComponent<FieldProps> {
  state = {};

  onChange = (value: string): void => {
    this.props.onChange(this.props.index, value);
  };

  render(): ReactNode {
    const { index, name, value, onRemove, disabled } = this.props;
    return (
      <div className={styles.field}>
        <input
          name={name}
          value={value}
          className={styles.input}
          onChange={(event): void => this.onChange(event.target.value)}
          disabled={disabled}
        />
        <Button
          variant="danger"
          noBorder
          wideBasis={false}
          icon={ICONS.CrossMark}
          onClick={(): void => onRemove(index)}
          className={styles.button}
          disabled={disabled}
        />
      </div>
    );
  }
}

/**
 * @deprecated Use `ArrayTextField` from `@iconik-io/uad` instead, or
 * `field.ArrayTextField` inside a `useAppForm` form. This component keeps its
 * own copy of `value`, has no label, description or error support, and its
 * buttons have no accessible names.
 */
type Props = {
  value?: ValueType;
  onChange?: (value: ValueType) => void;
  name?: string;
  className?: string;
  hideTooltip?: () => void;
  placeholder?: string;
  disabled?: boolean;
};

type State = {
  values: string[];
  tempValue?: string;
};

class TextMulti extends PureComponent<Props, State> {
  static defaultProps = {
    value: [],
  };

  state: State = {
    values: this.props.value ?? [],
  };

  onAdd = (): void => {
    this.setState({ tempValue: undefined });
  };

  onValueChange = (i: number, val: string): void => {
    const values = this.updateValues(val, i);
    this.setState({ values });
    this.props.onChange?.(values);
  };

  onFieldChange = (val: string): void => {
    let values;
    if (this.state.tempValue === undefined) {
      values = this.addValue(val);
    } else {
      values = this.updateValues(val, this.state.values.length - 1);
    }
    this.setState({ values });
    this.props.onChange?.(values);
    this.setState({ tempValue: val });
  };

  onRemove = (i: number): void => {
    const values = [...this.state.values];
    values.splice(i, 1);
    this.setState({ values });
    this.props.onChange?.(values);
  };

  getFields(): ReactNode {
    const items = this.state.values.map((_item, i) => {
      const name = `${i}.${this.props.name}`;
      return (
        <Field
          {...this.props}
          key={name}
          index={i}
          name={name}
          value={this.state.values[i]}
          onChange={this.onValueChange}
          onRemove={this.onRemove}
        />
      );
    });
    if (this.state.tempValue !== undefined) {
      items.pop();
    }
    return items;
  }

  getNewField(): ReactNode {
    return (
      <div className={styles.field}>
        <input
          name={this.props.name}
          value={this.state.tempValue || ''}
          className={styles.input}
          onChange={(event): void => this.onFieldChange(event.target.value)}
          onKeyDown={this.keyDown}
          onBlur={this.props.hideTooltip}
          placeholder={this.props.placeholder}
          disabled={this.props.disabled}
        />
        <Button
          disabled={this.props.disabled}
          variant="success"
          noBorder
          wideBasis={false}
          icon={ICONS.Plus}
          onClick={this.onAdd}
          className={styles.button}
        />
      </div>
    );
  }

  keyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.keyCode === 13) {
      // new field added when hit enter
      event.preventDefault();
      event.stopPropagation();
      this.onAdd();
    }
  };

  updateValues = (value: string, index: number): string[] =>
    this.state.values.map((v, i) => (i === index ? value : v));

  addValue = (value: string): string[] => [...this.state.values, value];

  render(): ReactNode {
    const fields = this.getFields();
    const newField = this.getNewField();

    return (
      <div className={this.props.className}>
        {fields}
        {newField}
      </div>
    );
  }
}

export default TextMulti;
